<template>
    <div 
        class="vod-genres"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <title-vod-genres/>
        <Lines-vod-genres-premium
            ref="linesVodGenres" 
            @ready="readyLines"
            @show-all="showAllItems"
            @list-item-selected="itemsSelected"
        />
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, onMounted, watch, nextTick } from "vue";
    import { useApplicationStore } from "@/stores/Application";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import type { TabsGenresCardData, OnDemandCardData } from "@/components/Cards/types";
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import type { LinesVodGenresPremiumRef } from "@/components/widgets/LinesVodGenresPremium/types";
    import { useRoute, useRouter } from 'vue-router'
    import LinesVodGenresPremium from "@/components/widgets/LinesVodGenresPremium/LinesVodGenresPremium.vue";
    import { Logger } from '@/utils';
    import { storeToRefs } from 'pinia';
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useFocus } from "@/composables/useFocus";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { AppConstants } from "@/common";
    import type {IEventSelected } from "@/components/RowView/types";
    import { Strings } from '@/locales/types/enums';
    import { ScreenName } from "@/common/types/screen-names";
    import { Param as AnalyticsParam } from "@/plugins/analytics/interfaces/analytics.service.interface";

    const { 
        setViewedDetailItems,
    } = useDetailPageStore();

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { setIsHaveFocusedElement, focusElementLine } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-vod-genres-premium'],
    });
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();

    const { $fire, $analytics, $navigator } = useNuxtApp();
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const traceOnDemandScreen = $fire.trace($fire.performance, 'ON_DEMAND_SCREEN_GENRES');
    try {
        traceOnDemandScreen.start();
        Logger.captureLog('ON_DEMAND_SCREEN_GENRES start');
    } catch (e){
        Logger.captureLog(e);
    }

    const linesVodGenres: Ref<LinesVodGenresPremiumRef | null> = ref(null);
    const isReadyLines: Ref<boolean> = ref(false);
    const isReadyAll: ComputedRef<boolean> = computed((): boolean => isReadyLines.value && linesVodGenres.value !== null);

    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const readyLines = (): void => {
        isReadyLines.value = true;
    };
    const itemsSelected = (val: IEventSelected): void => {
        if (val.lineId === shelfKeys.genresCards) {
            gotoTabGenresDetailsPage(val.item as TabsGenresCardData);
        } else if (val.lineId === shelfKeys.subChannelCards) {
            channelSelected(val.item as TabsGenresCardData);
        } else {
            gotoGenreDetailsPage(val.item as OnDemandCardData);
        }
    };
    const gotoTabGenresDetailsPage = (item: TabsGenresCardData): void => {
        void router.push(`${linkItems.detailGenre.routerPath}/${item.getGenreId()}`);
    };
    const channelSelected = (item: TabsGenresCardData) => {
        if (item.getIsPremium()) {
            void router.push(`${linkItems.subgenresPremium.routerPath}/${item.getId()}`);
            return;
        }
        void router.push(`${linkItems.subgenres.routerPath}/${item.getGenreId()}`);
    };
    const gotoGenreDetailsPage = (item: OnDemandCardData) => {
        try {
            const itemId = item.getId();
            if (!itemId) {
                throw new Error("Item id is null:" + JSON.stringify(item));
            }
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);

            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${itemId}` :
                `${linkItems.detailsVodMovies.routerPath}/${itemId}`;

            void router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const showAllItems = (val: IEventSelected): void => {
        if (val.lineId === shelfKeys.moviesCards) {
            void router.push(linkItems.showAllVodAllMovies.routerPath);
            return;
        }
        if (!val.genre?.id) {
            throw new Error('Genre id is not defined');
        }
        showAllVodSubGenres(val.genre.id);
    };
    const showAllVodSubGenres = (subGenreId: number): void => {
        void router.push(`${linkItems.showAllForGenre.routerPath}?subGenreId=${subGenreId}`);
    };
    const focusElement = (): void => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusFirstRow();
        }
    };
    const focusFirstRow = (): void => {
        if (linesVodGenres.value) {
            linesVodGenres.value.focusFirstRow();
        }
    };

    onMounted(async () => {
        void nextTick((): void => {
            $analytics.createCustomScreenEvent({
                [AnalyticsParam.FA_SCREEN_NAME]: ScreenName.VOD_CHANNELS_PREMIUM,
                [AnalyticsParam.FA_SCREEN_CLASS]: ScreenName.VOD_CHANNELS_PREMIUM,
                [AnalyticsParam.CHANNEL_ID]: Number(route.params.id),
            })
        });
    });

    const handlerReady = (): void => {
        void nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value) {
                focusElement();
            }
        });

        if (traceOnDemandScreen) {
            try {
                traceOnDemandScreen.stop();
                Logger.captureLog(`ON_DEMAND_SCREEN_GENRES stop ${traceOnDemandScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    };

    watch(isReadyAll, (value: boolean) => {
        if (value){
            handlerReady();
        }
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>